<template>
    <Head :title="`Pair Perspective${comparison ? ` · ${comparison.colleague_name}` : ''}`" />
    <BaseLayout gap="sm">
        <BaseLink :href="connection_url" class="body-small inline-flex w-fit items-center gap-2 text-ink-muted underline underline-offset-4">
            <i class="bi bi-arrow-left" aria-hidden="true" /> Back to {{ comparison?.colleague_name || "your colleague" }}
        </BaseLink>
        <header class="flex items-start justify-between gap-6 border-b border-border pb-4 md:pb-10">
            <div class="flex min-w-0 flex-col gap-3">
                <p class="scaffold-body-small text-ink-muted">Pair Perspective</p>
                <h1 class="display-serif text-ink">{{ comparison ? `You and ${comparison.colleague_name}` : "Your working dynamic" }}</h1>
                <p class="body max-w-140 text-ink-muted">Understand your working dynamic.</p>
            </div>
            <PairPerspectiveMark :viewer-archetype="comparison?.viewer_archetype" :colleague-archetype="comparison?.colleague_archetype" class="hidden shrink-0 md:block" />
        </header>
        <template v-if="comparison">
            <div class="flex flex-col gap-4 md:gap-6">
                <div class="grid grid-cols-2 gap-4 md:gap-6">
                    <div class="flex min-w-0 items-center gap-3">
                        <PerspectiveAvatar :letters="getPerspectiveLetters(comparison.viewer_archetype)" />
                        <div class="min-w-0">
                            <p class="body-semibold text-ink">You</p>
                            <p v-if="comparison.viewer_archetype" class="body-small text-ink-muted">{{ comparison.viewer_archetype }}</p>
                        </div>
                    </div>
                    <div class="flex min-w-0 items-center gap-3">
                        <PerspectiveAvatar :name="comparison.colleague_name" :letters="getPerspectiveLetters(comparison.colleague_archetype)" />
                        <div class="min-w-0">
                            <p class="body-semibold break-words text-ink">{{ comparison.colleague_name }}</p>
                            <p v-if="comparison.colleague_archetype" class="body-small text-ink-muted">{{ comparison.colleague_archetype }}</p>
                        </div>
                    </div>
                </div>
                <p class="body-small max-w-170 text-ink-muted">Explore how your working preferences come together. Differences can be strengths.</p>
            </div>
            <PairPerspectiveDimensions :comparison="comparison" :is-starting-chat="isStartingChat" :chat-error="chatError" @discuss="discuss" />
        </template>
        <BaseCard v-else padding="lg"
            ><p class="body text-ink-muted">{{ pair.unavailable_reason }}</p></BaseCard
        >
    </BaseLayout>
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head } from "@inertiajs/vue3";
import type { PairPerspectiveResponse } from "~vue/api/generated/pair-perspective";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import PerspectiveAvatar from "~vue/components/PerspectiveAvatar.vue";
import { usePairPerspectiveDiscussion } from "~vue/composables/usePairPerspectiveDiscussion";
import PairPerspectiveDimensions from "~vue/Connections/PairPerspectiveDimensions.vue";
import PairPerspectiveMark from "~vue/Connections/PairPerspectiveMark.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { getPerspectiveLetters } from "~vue/utils/perspectiveUtils";
import { computed } from "vue";

const props = defineProps<{ pair: PairPerspectiveResponse; connection_id: string; connection_url: string }>();
const comparison = computed(() => props.pair.comparison);
const { discuss, isStartingChat, chatError } = usePairPerspectiveDiscussion(() => ({ id: props.connection_id }));
</script>
